import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";

import {
  AddOutlined,
  CloseRounded,
  NoteAddOutlined,
  Search,
} from "@mui/icons-material";
import {
  Box,
  ButtonBase,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import PropTypes from "prop-types";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { sidebarClear } from "../../../../redux/actions/sidebarActions";
import { slate, teal } from "../../../../theme/colors";
import { FORM, formSx, px } from "../../../../theme/formStyles";
import { useStageRecordFiles } from "../../shared/RecordDocumentsDropzone";
import DrilldownSidebarPane from "../DrilldownSidebarPane";
import {
  CATEGORIES,
  matchesSearch,
  useAddToLoanItems,
} from "./addToLoan/addToLoanCatalog";

/**
 * "Add to loan" — the drilldown's quick-add panel, on the panel-form shell
 * (FORM_STYLE_GUIDE.md §12b): pinned header (title, loan facts, search and the
 * category chips), a scrolling body of compact tiles, and a pinned footer of
 * keyboard hints.
 *
 * The catalog, its gating and what a pick does live in
 * `addToLoan/addToLoanCatalog.js`, shared with the Quick Add rail button's
 * hover menu. Picking a tile opens that item's own sidebar form.
 *
 * Keyboard: `/` focuses search · arrows move across the tile grid · Enter
 * adds the highlighted tile · Esc clears the search, then closes the panel.
 * Files dropped anywhere on the body are staged into the reusable upload
 * modal's review queue — nothing uploads on drop.
 */

const ALL = "all";
const SUGGESTED_COUNT = 6;
const EMPTY_TRIES = ["fee", "attorney", "wire", "reserve"];
const SIDE = px(FORM.s.s55);
const SIDE_NARROW = px(18);
const NARROW = "@container (max-width: 520px)";

// ── Small parts ───────────────────────────────────────────────────────

const Kbd = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      font: "inherit",
      fontSize: 11,
      fontWeight: 500,
      lineHeight: 1,
      color: slate[500],
      background: "#fff",
      border: `1px solid ${slate[300]}`,
      borderBottomWidth: 2,
      borderRadius: "4px",
      px: "5px",
      py: "2px",
    }}
  >
    {children}
  </Box>
);

Kbd.propTypes = { children: PropTypes.node };

/** The first occurrence of the query, marked — the design's search echo. */
const Highlight = ({ text, query }) => {
  const q = query.trim();
  const i = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <Box
        component="mark"
        sx={{ background: "transparent", color: teal[700], fontWeight: 600 }}
      >
        {text.slice(i, i + q.length)}
      </Box>
      {text.slice(i + q.length)}
    </>
  );
};

Highlight.propTypes = { text: PropTypes.string, query: PropTypes.string };

const FilterChip = ({ label, count, on, disabled, onClick }) => (
  <ButtonBase
    disableRipple
    aria-pressed={on}
    disabled={disabled}
    onClick={onClick}
    sx={{
      flex: "none",
      height: 28,
      px: "10px",
      gap: "6px",
      borderRadius: "999px",
      border: `1px solid ${on ? "transparent" : slate[200]}`,
      background: on ? teal[100] : "#fff",
      color: on ? teal[700] : slate[500],
      fontSize: 12.5,
      fontWeight: 500,
      "&:hover": on ? {} : { borderColor: slate[300], color: slate[900] },
      "&.Mui-disabled": { opacity: 0.4 },
    }}
  >
    {label}
    <Box
      component="span"
      sx={{
        fontSize: 11,
        color: on ? teal[700] : slate[400],
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {count}
    </Box>
  </ButtonBase>
);

FilterChip.propTypes = {
  label: PropTypes.node,
  count: PropTypes.number,
  on: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};

const SuggestButton = ({ item, onSelect }) => {
  const Icon = item.icon;
  return (
    <ButtonBase
      disableRipple
      onClick={() => onSelect(item)}
      sx={{
        height: 30,
        pl: 1,
        pr: "10px",
        gap: "6px",
        borderRadius: px(FORM.radius.control),
        border: `1px dashed ${slate[300]}`,
        color: slate[900],
        fontSize: 12.5,
        fontWeight: 500,
        "& .MuiSvgIcon-root": { fontSize: 15, color: slate[500] },
        "&:hover, &:focus-visible": {
          borderStyle: "solid",
          borderColor: teal[600],
          background: teal[50],
          color: teal[700],
          "& .MuiSvgIcon-root": { color: teal[700] },
        },
      }}
    >
      <Icon />
      {item.label}
    </ButtonBase>
  );
};

SuggestButton.propTypes = {
  item: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const GroupHeading = ({ children, count, sx }) => (
  <Flx
    sx={{
      alignItems: "baseline",
      gap: "6px",
      fontSize: 12.5,
      fontWeight: 600,
      color: slate[500],
      mt: 3.5,
      mb: 1.25,
      ...sx,
    }}
  >
    {children}
    {count != null && (
      <Box component="span" sx={{ fontWeight: 400, color: slate[400] }}>
        {count}
      </Box>
    )}
  </Flx>
);

GroupHeading.propTypes = {
  children: PropTypes.node,
  count: PropTypes.number,
  sx: PropTypes.object,
};

const Tile = memo(
  ({ item, count, query, active, tileRef, onSelect, onHover }) => {
    const Icon = item.icon;
    return (
      <ButtonBase
        ref={tileRef}
        disableRipple
        onClick={() => onSelect(item)}
        onMouseMove={onHover}
        className={active ? "is-active" : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "10px",
          textAlign: "left",
          minHeight: 52,
          px: "10px",
          py: 1,
          borderRadius: "9px",
          border: `1px solid ${slate[200]}`,
          background: "#fff",
          transition: "border-color .12s, box-shadow .12s",
          "& .qa-ic": {
            width: 30,
            height: 30,
            flex: "none",
            borderRadius: "7px",
            display: "grid",
            placeItems: "center",
            background: slate[100],
            color: slate[500],
            transition: "background .12s, color .12s",
            "& .MuiSvgIcon-root": { fontSize: 17 },
          },
          "& .qa-add": {
            fontSize: 17,
            color: teal[700],
            opacity: 0,
            transform: "translateX(-3px)",
            transition: "opacity .12s, transform .12s",
          },
          "&:hover, &.is-active, &:focus-visible": {
            borderColor: teal[600],
            "& .qa-ic": { background: teal[100], color: teal[700] },
            "& .qa-add": { opacity: 1, transform: "none" },
          },
          "&.is-active": { boxShadow: `0 0 0 3px ${teal[200]}` },
        }}
      >
        <span className="qa-ic">
          <Icon />
        </span>
        <Flx column sx={{ flex: 1, minWidth: 0 }}>
          <Flx ac sx={{ gap: "6px" }}>
            <Txt sx={{ fontSize: 13.5, fontWeight: 500, color: slate[900] }}>
              <Highlight text={item.label} query={query} />
            </Txt>
            {count > 0 && (
              <Box
                component="span"
                title={`${count} on this loan`}
                sx={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: slate[500],
                  background: slate[100],
                  borderRadius: "999px",
                  px: "7px",
                  py: "1px",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {count}
              </Box>
            )}
          </Flx>
          <Txt
            sx={{
              fontSize: 12,
              color: slate[500],
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Highlight text={item.description} query={query} />
          </Txt>
        </Flx>
        <AddOutlined className="qa-add" />
      </ButtonBase>
    );
  },
);

Tile.displayName = "Tile";
Tile.propTypes = {
  item: PropTypes.object.isRequired,
  count: PropTypes.number,
  query: PropTypes.string,
  active: PropTypes.bool,
  tileRef: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  onHover: PropTypes.func,
};

// ── Header pieces ─────────────────────────────────────────────────────

const HeaderFacts = ({ loan }) => {
  const borrowers = loan?.borrowers?.length || 0;
  const properties = loan?.subjectProperties?.length || 0;
  const facts = [
    loan?.loanProductType,
    borrowers ? `${borrowers} ${borrowers === 1 ? "applicant" : "applicants"}` : null,
    properties
      ? `${properties} ${properties === 1 ? "property" : "properties"}`
      : null,
  ].filter(Boolean);

  return (
    <Flx wrap sx={{ gap: "4px 10px", mt: "2px", fontSize: 12.5 }}>
      {loan?.loanNumber && (
        <Txt sx={{ fontSize: 12.5, fontWeight: 500, color: slate[900] }}>
          Loan #{loan.loanNumber}
        </Txt>
      )}
      {facts.map((fact) => (
        <Txt key={fact} sx={{ fontSize: 12.5, color: slate[500] }}>
          {fact}
        </Txt>
      ))}
    </Flx>
  );
};

HeaderFacts.propTypes = { loan: PropTypes.object };

const FooterHints = () => (
  <Flx
    ac
    sx={{
      ...formSx.panelFoot,
      py: "10px",
      gap: "14px",
      bgcolor: slate[50],
      fontSize: 12,
      color: slate[500],
      "& > span": { display: "inline-flex", alignItems: "center", gap: "5px" },
      [NARROW]: { display: "none" },
    }}
  >
    <span>
      <Kbd>↑</Kbd>
      <Kbd>↓</Kbd> Move
    </span>
    <span>
      <Kbd>↵</Kbd> Add
    </span>
    <span>
      <Kbd>/</Kbd> Search
    </span>
    <Box sx={{ flex: 1 }} />
    <span>
      <Kbd>Esc</Kbd> Close
    </span>
  </Flx>
);

// ── Panel ─────────────────────────────────────────────────────────────

const DrilldownCreateNewItemSidebar = memo(() => {
  const dispatch = useDispatch();
  const { loan, available, select, suggested, countOf } = useAddToLoanItems();

  const [query, setQuery] = useState("");
  const [cat, setCat] = useState(ALL);
  const [active, setActive] = useState(0);
  // The highlight only shows once the user is driving by keyboard or search,
  // so a freshly opened panel doesn't look like something is pre-selected.
  const [kbd, setKbd] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const dragDepth = useRef(0);
  const searchRef = useRef(null);
  const headRef = useRef(null);
  const bodyRef = useRef(null);
  const tileRefs = useRef([]);
  const scrollOnMove = useRef(false);

  // Files land in the upload modal's review queue (this panel has no
  // UploadQueueProvider of its own), assigned to the loan — the queue's own
  // picker can still re-target them to a borrower or property.
  const stageFiles = useStageRecordFiles(null);
  const takeFiles = useCallback(
    (fileList) => {
      if (!fileList?.length) return;
      stageFiles(fileList);
      dispatch(sidebarClear());
    },
    [stageFiles, dispatch],
  );

  // Search first, then the category: chip counts reflect what search left.
  const searched = useMemo(
    () => available.filter((item) => matchesSearch(item, query)),
    [available, query],
  );

  const countsByCategory = useMemo(() => {
    const counts = {};
    searched.forEach((item) => {
      counts[item.category] = (counts[item.category] || 0) + 1;
    });
    return counts;
  }, [searched]);

  // Searching can empty the selected category; fall back to All rather than
  // stranding the panel on a blank view.
  const activeCat = cat !== ALL && !countsByCategory[cat] ? ALL : cat;

  const groups = useMemo(
    () =>
      CATEGORIES.filter((c) => activeCat === ALL || activeCat === c.key)
        .map((c) => ({
          ...c,
          items: searched.filter((item) => item.category === c.key),
        }))
        .filter((g) => g.items.length),
    [activeCat, searched],
  );

  // One flat order for keyboard movement across every visible group.
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const activeIndex = Math.min(active, Math.max(0, flat.length - 1));
  const showActive = (kbd || !!query) && flat.length > 0;

  const suggestions = useMemo(
    () => (!query && activeCat === ALL ? suggested(SUGGESTED_COUNT) : []),
    [query, activeCat, suggested],
  );

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!scrollOnMove.current) return;
    scrollOnMove.current = false;
    tileRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const changeQuery = useCallback((q) => {
    setQuery(q);
    setActive(0);
  }, []);

  // Tiles per row, read off the rendered grid so arrow-down lands one row down
  // at whatever width the panel has been dragged to.
  const columns = () => {
    const grid = bodyRef.current?.querySelector("[data-qa-grid]");
    if (!grid) return 1;
    return getComputedStyle(grid).gridTemplateColumns.split(" ").length || 1;
  };

  // The panel's keys, listened for on the document so they work whether focus
  // is in the search box, on a tile, or nowhere at all.
  useEffect(() => {
    const onKey = (e) => {
      const el = document.activeElement;
      const ours =
        el === document.body ||
        headRef.current?.contains(el) ||
        bodyRef.current?.contains(el);
      if (!ours || e.metaKey || e.ctrlKey || e.altKey) return;

      const inSearch = el === searchRef.current;
      const inField =
        /INPUT|TEXTAREA|SELECT/.test(el?.tagName) || el?.isContentEditable;

      if (e.key === "/" && !inField) {
        e.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (e.key === "Escape" && query) {
        // Clear first; the sidebar's own Esc handler closes on the next press.
        e.preventDefault();
        e.stopImmediatePropagation();
        changeQuery("");
        return;
      }
      if (!flat.length) return;

      const c = columns();
      const move = {
        ArrowDown: c,
        ArrowUp: -c,
        ArrowRight: inSearch ? 0 : 1,
        ArrowLeft: inSearch ? 0 : -1,
      }[e.key];
      if (move) {
        e.preventDefault();
        scrollOnMove.current = true;
        setKbd(true);
        // The first arrow press only reveals the highlight on the first tile.
        setActive((i) =>
          showActive ? Math.max(0, Math.min(flat.length - 1, i + move)) : 0,
        );
        return;
      }
      if (e.key === "Enter" && (inSearch || !inField) && el?.tagName !== "BUTTON") {
        e.preventDefault();
        select(flat[showActive ? activeIndex : 0]);
      }
    };
    // Capture phase, so an Esc that clears the search never reaches the
    // sidebar's close handler.
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [flat, activeIndex, showActive, query, select, changeQuery]);

  tileRefs.current = [];
  let tileIndex = -1;

  const header = (
    <Box
      ref={headRef}
      sx={{ px: SIDE, pb: "12px", [NARROW]: { px: SIDE_NARROW } }}
    >
      <TextField
        fullWidth
        inputRef={searchRef}
        placeholder="Search: fee, attorney, wire, condition…"
        value={query}
        onChange={(e) => changeQuery(e.target.value)}
        inputProps={{ "aria-label": "Search items to add" }}
        sx={formSx.input}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ fontSize: 18, color: slate[400] }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {query ? (
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => {
                    changeQuery("");
                    searchRef.current?.focus();
                  }}
                >
                  <CloseRounded sx={{ fontSize: 16 }} />
                </IconButton>
              ) : (
                <Kbd>/</Kbd>
              )}
            </InputAdornment>
          ),
        }}
      />
      <Flx
        role="group"
        aria-label="Filter by category"
        sx={{
          gap: "6px",
          mt: "10px",
          overflowX: "auto",
          scrollbarWidth: "none",
          pb: "1px",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <FilterChip
          label="All"
          count={searched.length}
          on={activeCat === ALL}
          onClick={() => {
            setCat(ALL);
            setActive(0);
          }}
        />
        {CATEGORIES.map((c) => {
          const n = countsByCategory[c.key] || 0;
          return (
            <FilterChip
              key={c.key}
              label={c.short}
              count={n}
              on={activeCat === c.key}
              disabled={n === 0 && activeCat !== c.key}
              onClick={() => {
                setCat(c.key);
                setActive(0);
              }}
            />
          );
        })}
      </Flx>
    </Box>
  );

  return (
    <DrilldownSidebarPane
      title="Add to loan"
      initialWidth="560px"
      descriptionContent={<HeaderFacts loan={loan} />}
      titleBottomContent={header}
      sx={formSx.panelContainer}
      headerSx={{
        ...formSx.panelHead,
        borderBottom: 0,
        padding: `${px(FORM.s.s4)} ${SIDE} ${px(FORM.s.s3)}`,
        "& .MuiTypography-h1": {
          ...formSx.panelHead["& .MuiTypography-h1"],
          fontSize: 17,
        },
      }}
      bodyPadding={0}
      bodySx={{
        background: "#fff",
        padding: `6px ${SIDE} 18px`,
        [NARROW]: { padding: `6px ${SIDE_NARROW} 18px` },
      }}
      footerContent={<FooterHints />}
    >
      <Box
        ref={bodyRef}
        sx={{ position: "relative", minHeight: "100%" }}
        onDragEnter={(e) => {
          e.preventDefault();
          dragDepth.current += 1;
          setDragActive(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={(e) => {
          e.preventDefault();
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragActive(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          dragDepth.current = 0;
          setDragActive(false);
          takeFiles(e.dataTransfer?.files);
        }}
      >
        {suggestions.length > 0 && (
          <Box sx={{ mt: 2, mb: 0.5 }}>
            <GroupHeading sx={{ m: 0 }}>Suggested</GroupHeading>
            <Flx wrap sx={{ gap: "6px", mt: 1 }}>
              {suggestions.map((item) => (
                <SuggestButton key={item.key} item={item} onSelect={select} />
              ))}
            </Flx>
          </Box>
        )}

        {groups.length === 0 ? (
          <Flx column center sx={{ py: 6, px: 1.5, textAlign: "center" }}>
            <Txt
              component="div"
              sx={{ fontSize: 14, fontWeight: 600, color: slate[900], mb: 0.5 }}
            >
              Nothing matches “{query}”
            </Txt>
            <Txt component="div" sx={{ fontSize: 13, color: slate[500] }}>
              Search by item name or what it covers.
            </Txt>
            <Flx wrap jc sx={{ gap: "6px", mt: 1.5 }}>
              {EMPTY_TRIES.map((t) => (
                <FilterChip
                  key={t}
                  label={t}
                  onClick={() => {
                    setCat(ALL);
                    changeQuery(t);
                    searchRef.current?.focus();
                  }}
                />
              ))}
            </Flx>
          </Flx>
        ) : (
          groups.map((g) => (
            <Box key={g.key}>
              <GroupHeading count={g.items.length}>{g.heading}</GroupHeading>
              <Box
                data-qa-grid
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(215px, 1fr))",
                  gap: "6px",
                }}
              >
                {g.items.map((item) => {
                  tileIndex += 1;
                  const i = tileIndex;
                  return (
                    <Tile
                      key={item.key}
                      item={item}
                      count={countOf(item)}
                      query={query}
                      active={showActive && i === activeIndex}
                      tileRef={(node) => {
                        tileRefs.current[i] = node;
                      }}
                      onSelect={select}
                      onHover={() => {
                        if (showActive && i !== activeIndex) setActive(i);
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          ))
        )}

        {/* Drop feedback — the whole body is the drop surface. */}
        {dragActive && (
          <Flx
            column
            center
            g={0.5}
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "10px",
              border: `2px dashed ${teal[400]}`,
              background: "rgba(242,250,251,.94)",
              pointerEvents: "none",
              zIndex: 2,
            }}
          >
            <NoteAddOutlined sx={{ fontSize: 26, color: teal[600] }} />
            <Txt component="div" sx={{ fontSize: 13.5, fontWeight: 600, color: teal[700] }}>
              Drop to upload to this loan
            </Txt>
            <Txt component="div" sx={{ fontSize: 12, color: slate[500] }}>
              You can review and assign the files before anything uploads
            </Txt>
          </Flx>
        )}
      </Box>
    </DrilldownSidebarPane>
  );
});

DrilldownCreateNewItemSidebar.displayName = "DrilldownCreateNewItemSidebar";

export default DrilldownCreateNewItemSidebar;
